import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Send, MessageCircle, Phone, Mail } from "lucide-react";
import { toast } from "sonner";
import { CONTACT, SERVICE_OPTIONS, whatsappLink, openExternal } from "../../lib/site";
import { Reveal } from "../site/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fieldClass =
  "w-full h-14 px-4 rounded-lg bg-forest border border-white/10 text-cream placeholder:text-cream/40 font-body focus:outline-none focus:border-brand-orange focus:ring-1 focus:ring-brand-orange transition-colors duration-200";

export const Contact = () => {
  const [form, setForm] = useState({
    name: "", phone: "", email: "", propertyAddress: "", service: "", message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const buildText = () =>
    `Name: ${form.name}\nPhone: ${form.phone}\nEmail: ${form.email}\nProperty Address: ${form.propertyAddress}\nService: ${form.service}\n\nMessage:\n${form.message}`;

  const handleEmail = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please add your name, email and a short message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name,
        phone: form.phone,
        email: form.email,
        property_address: form.propertyAddress,
        service: form.service,
        message: form.message,
      });
      toast.success("Thanks! Your enquiry has been sent — we'll be in touch soon.");
      setForm({ name: "", phone: "", email: "", propertyAddress: "", service: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try WhatsApp or call us instead.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleWhatsApp = () => {
    const text = `Hi Bezalel Property Services, I'd like a quote.\n\n${buildText()}`;
    openExternal(whatsappLink(text));
  };

  return (
    <section id="contact" className="relative bg-forest py-24 md:py-32 scroll-mt-20" data-testid="contact-section">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <Reveal className="max-w-2xl mb-14">
          <span className="font-display uppercase tracking-[0.25em] text-xs text-brand-orange">Get In Touch</span>
          <h2 className="mt-4 font-display uppercase tracking-tight text-5xl sm:text-6xl lg:text-7xl text-cream leading-[0.95]">
            Request a <span className="text-brand-orange">free quote</span>
          </h2>
          <p className="mt-5 text-cream/70 leading-relaxed">
            Tell us about your project and we'll get straight back to you. Prefer to talk?
            Message us on WhatsApp or tap to call.
          </p>
        </Reveal>

        <div className="grid gap-6 lg:grid-cols-12">
          <Reveal className="lg:col-span-7">
            <form onSubmit={handleEmail} className="rounded-2xl border border-white/10 bg-forest-surface p-6 sm:p-8" data-testid="contact-form">
              <div className="grid gap-4 sm:grid-cols-2">
                <input className={fieldClass} placeholder="Full Name *" value={form.name} onChange={set("name")} data-testid="contact-name" />
                <input className={fieldClass} placeholder="Phone" type="tel" value={form.phone} onChange={set("phone")} data-testid="contact-phone" />
                <input className={fieldClass} placeholder="Email *" type="email" value={form.email} onChange={set("email")} data-testid="contact-email" />
                <input className={fieldClass} placeholder="Property Address" value={form.propertyAddress} onChange={set("propertyAddress")} data-testid="contact-address" />
                <select className={`${fieldClass} sm:col-span-2 appearance-none`} value={form.service} onChange={set("service")} data-testid="contact-service">
                  <option value="">Select a service</option>
                  {SERVICE_OPTIONS.map((s) => (
                    <option key={s} value={s} className="bg-forest text-cream">{s}</option>
                  ))}
                </select>
                <textarea rows={5} className={`${fieldClass} h-auto py-3 sm:col-span-2 resize-none`} placeholder="Tell us about your project *" value={form.message} onChange={set("message")} data-testid="contact-message" />
              </div>

              <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <button type="submit" disabled={submitting} className="inline-flex items-center justify-center gap-2 h-14 px-8 rounded-full bg-brand-orange hover:bg-brand-orangeHover disabled:opacity-60 text-white font-display uppercase tracking-wider text-base transition-[background-color,transform] duration-300 hover:-translate-y-1" data-testid="contact-send-query">
                  <Send className="h-5 w-5" /> {submitting ? "Sending…" : "Send Query"}
                </button>
                <button type="button" onClick={handleWhatsApp} className="inline-flex items-center justify-center gap-2 h-14 px-8 rounded-full bg-[#25D366] hover:bg-[#1fb959] text-white font-display uppercase tracking-wider text-base transition-[background-color,transform] duration-300 hover:-translate-y-1" data-testid="contact-whatsapp">
                  <MessageCircle className="h-5 w-5" /> Chat on WhatsApp
                </button>
              </div>
              <p className="mt-4 text-cream/45 text-xs">Your enquiry is sent straight to our inbox. Prefer to talk? Message us on WhatsApp or call {CONTACT.phoneDisplay}.</p>
            </form>
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-5 flex flex-col gap-4">
            <a href={`tel:${CONTACT.phoneTel}`} className="group flex items-center gap-4 rounded-2xl border border-white/10 bg-forest-surface p-6 hover:border-brand-orange/50 transition-colors duration-300" data-testid="contact-tel-card">
              <span className="grid place-items-center h-14 w-14 rounded-full bg-brand-orange/15 text-brand-orange shrink-0"><Phone className="h-6 w-6" /></span>
              <span>
                <span className="block font-display uppercase tracking-[0.14em] text-xs text-cream/50">Call Us Today</span>
                <span className="block font-display text-2xl text-cream tracking-wide">{CONTACT.phoneDisplay}</span>
              </span>
            </a>
            <a href={`mailto:${CONTACT.email}`} className="group flex items-center gap-4 rounded-2xl border border-white/10 bg-forest-surface p-6 hover:border-brand-orange/50 transition-colors duration-300" data-testid="contact-email-card">
              <span className="grid place-items-center h-14 w-14 rounded-full bg-brand-orange/15 text-brand-orange shrink-0"><Mail className="h-6 w-6" /></span>
              <span className="min-w-0">
                <span className="block font-display uppercase tracking-[0.14em] text-xs text-cream/50">Email</span>
                <span className="block text-cream break-all">{CONTACT.email}</span>
              </span>
            </a>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
